import React, { useEffect, useState, useRef } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Button } from '../../components/ui/button';
import { jsPDF } from 'jspdf';
import { robotoRegularBase64 } from './roboto';
import { robotoBoldBase64 } from './roboto';
import { Link } from 'react-router-dom';
import { FileText, Eye, ChevronDown, Download } from 'lucide-react';

const ECCWorkPermitPDFViewer = ({ workpermit, instructions, isOpen, onClose, onPdfGenerated }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedInstructionType, setSelectedInstructionType] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pdfViewerRef = useRef(null);

  const cleanup = () => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  const getAttachmentFileName = () => {
    return workpermit?.work_permit_number ? `workpermit_${workpermit.work_permit_number}.pdf` : 'N/A';
  };

  const formatTeamLeaderData = (teamData) => {
    if (!teamData || !teamData.team_leader || !teamData.users || !Array.isArray(teamData.users)) {
      return 'N/A';
    }

    const teamLeader = teamData.users.find(user => user.id === teamData.team_leader);
    if (!teamLeader) {
      return 'N/A';
    }

    return `${teamLeader.first_name || ''} ${teamLeader.last_name || ''}`.trim() || 'N/A';
  };

  const generatePDF = async () => {
    if (!workpermit || !instructions || selectedInstructionType === null) {
      console.log('Brak wymaganych danych do generowania PDF:', { workpermit, instructions, selectedInstructionType });
      cleanup();
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('Rozpoczęcie generowania PDF z typem instrukcji:', selectedInstructionType);
      const doc = new jsPDF('p', 'mm', 'a4');

      const selectedInstruction = instructions.find(instruction =>
        selectedInstructionType === 'proper' ? instruction.proper : !instruction.proper
      );

      if (!selectedInstruction) {
        throw new Error(`Nie znaleziono instrukcji typu: ${selectedInstructionType}`);
      }

      doc.setLanguage('pl');
      doc.addFileToVFS('Roboto-Regular.ttf', robotoRegularBase64);
      doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
      doc.addFileToVFS('Roboto-Bold.ttf', robotoBoldBase64);
      doc.addFont('Roboto-Bold.ttf', 'Roboto', 'bold');

      const styles = {
        headerBg: [242, 242, 242],
        tableHeaderBg: [235, 241, 247],
        grayBg: [220, 220, 220],
        blueBg: [200, 220, 240]
      };

      const headerFontSize = 6.5;
      const sectionTitleFontSize = 8;
      const normalFontSize = 8;

      const measureTextHeight = (text, maxWidth) => {
        const textStr = text?.toString() || '';
        const lines = doc.splitTextToSize(textStr, maxWidth);
        return lines.length * 4;
      };

      const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('pl-PL', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      };

      const addPageHeader = () => {
        doc.setFillColor(...styles.headerBg);
        doc.rect(0, 0, 210, 12, 'F');

        doc.setFontSize(headerFontSize);
        doc.setFont('Roboto', 'bold');
        doc.text("Doc:", 5, 8);
        doc.setFont('Roboto', 'normal');
        doc.text(workpermit?.work_permit_number?.toString() || 'N/A', 15, 8, { maxWidth: 30 });

        doc.setFont('Roboto', 'bold');
        doc.text("Document owner:", 45, 8);
        doc.setFont('Roboto', 'normal');
        doc.text(formatTeamLeaderData(workpermit?.team_data) || 'N/A', 65, 8, { maxWidth: 30 });

        doc.setFont('Roboto', 'bold');
        doc.text("Site Doc:", 100, 8);
        doc.setFont('Roboto', 'normal');
        doc.text(workpermit?.turbine_data?.location?.toString() || 'N/A', 115, 8, { maxWidth: 40 });

        doc.setFont('Roboto', 'bold');
        doc.text("Approved by:", 160, 8);
        doc.setFont('Roboto', 'normal');
        doc.text(workpermit?.approver_name?.toString() || 'N/A', 175, 8, { maxWidth: 30 });

        doc.setFontSize(12);
        doc.text("Polecenie/Pozwolenie wykonania pracy", 105, 22, { align: 'center' });

        doc.setFontSize(7);
        doc.setFont('Roboto', 'normal');
        doc.text("Vestas Poland Sp. z o.o. · al. Niepodleglosci 44 · 70-404 Szczecin · Poland", 105, 28, { align: 'center' });

        return 35;
      };

      const addRow = (yPos, label, value, height = 12) => {
        doc.rect(10, yPos, 190, height);
        doc.setFontSize(normalFontSize);
        doc.setFont('Roboto', 'bold');
        doc.text(label, 12, yPos + 5);
        doc.setFont('Roboto', 'normal');
        const textValue = value?.toString() || 'N/A';
        doc.text(textValue, 12, yPos + height - 4, { maxWidth: 180 });
        return yPos + height;
      };

      const addSplitRow = (yPos, label1, value1, label2, value2, height = 12) => {
        doc.rect(10, yPos, 95, height);
        doc.rect(105, yPos, 95, height);
        doc.setFontSize(normalFontSize);
        doc.setFont('Roboto', 'bold');
        doc.text(label1, 12, yPos + 5);
        doc.text(label2, 107, yPos + 5);
        doc.setFont('Roboto', 'normal');
        const textValue1 = value1?.toString() || 'N/A';
        const textValue2 = value2?.toString() || 'N/A';
        doc.text(textValue1, 12, yPos + height - 4, { maxWidth: 85 });
        doc.text(textValue2, 107, yPos + height - 4, { maxWidth: 85 });
        return yPos + height;
      };

      const addFirstPage = () => {
        let yPos = addPageHeader();

        doc.setFillColor(...styles.blueBg);
        doc.rect(10, yPos, 190, 8, 'F');
        doc.setFontSize(sectionTitleFontSize);
        doc.setFont('Roboto', 'bold');
        doc.text("Dane dotyczace zadania / Work details", 12, yPos + 6);
        yPos += 10;

        yPos = addRow(yPos, "Zatwierdzony / Approved by", workpermit.approver_name?.toString() || 'N/A');

        yPos = addSplitRow(
          yPos,
          "Numer polecenia / Work permit number",
          workpermit?.work_permit_number?.toString() || 'N/A',
          "Data / date",
          formatDate(workpermit?.approve_date)
        );

        yPos = addRow(yPos, "Kierujacy zespolem / Work responsible",
          formatTeamLeaderData(workpermit?.team_data));

        yPos = addSplitRow(
          yPos,
          "Planowany termin rozpoczecia",
          formatDate(workpermit?.planned_date_start),
          "Planowany termin zakonczenia",
          formatDate(workpermit?.planned_date_end)
        );

        yPos = addRow(yPos, "Planowany czas pracy / Planned work time", "");
        yPos = addRow(yPos, "Planowane przerwy / Planned breaks", "");

        yPos = addSplitRow(
          yPos,
          "Koordynujacy",
          workpermit?.company_name?.toString() || 'N/A',
          "Koordynator",
          `Company ${workpermit?.company_to?.toString() || 'N/A'}`
        );

        yPos = addRow(yPos, "Strefa pracy, opis granic LOTO / Scope of works",
          workpermit?.work_scope_data?.name?.toString() || 'N/A', 12);

        yPos = addRow(yPos, "Zalaczniki / Reference documents",
          getAttachmentFileName(), 12);

        yPos += 2;
        doc.setFillColor(...styles.blueBg);
        doc.rect(10, yPos, 190, 8, 'F');
        doc.setFontSize(sectionTitleFontSize);
        doc.setFont('Roboto', 'bold');
        doc.text("Zespol / Team", 12, yPos + 6);
        yPos += 10;

        if (workpermit?.team_data?.users) {
          const users = workpermit.team_data.users;
          for (let i = 0; i < 8; i += 2) {
            const user1 = users[i];
            const user2 = users[i + 1];

            doc.rect(10, yPos, 95, 15);
            doc.rect(105, yPos, 95, 15);

            doc.setFontSize(normalFontSize);
            doc.setFont('Roboto', 'bold');
            doc.text(`Osoba ${i + 1} / Person ${i + 1}`, 12, yPos + 5);
            doc.text(`Osoba ${i + 2} / Person ${i + 2}`, 107, yPos + 5);

            doc.setFont('Roboto', 'normal');
            doc.text(user1 ? `${user1.first_name} ${user1.last_name}` : 'N/A', 12, yPos + 10, { maxWidth: 85 });
            doc.text(user2 ? `${user2.first_name} ${user2.last_name}` : 'N/A', 107, yPos + 10, { maxWidth: 85 });

            yPos += 15;
          }
        }

        return yPos;
      };

      const addSecondPage = () => {
        doc.addPage();
        let yPos = addPageHeader();

        doc.setFillColor(...styles.blueBg);
        doc.rect(10, yPos, 190, 10, 'F');
        doc.setFontSize(sectionTitleFontSize);
        doc.setFont('Roboto', 'bold');
        doc.text("Zakres prac pozwolenia / polecenia wykonania pracy / Scope of the Work Permit", 15, yPos + 7);
        yPos += 15;

        const turbineData = [
          ["Model turbiny / Turbine model", workpermit?.turbine_data?.model_data?.name || 'N/A'],
          ["Numer seryjny turbiny / Turbine serial number", workpermit?.turbine_data?.turbine_number?.toString() || 'N/A'],
          ["Lokalizacja turbiny / Turbine location", workpermit?.turbine_data?.location?.toString() || 'N/A'],
          ["Ordery / Orders", workpermit?.order_number?.toString() || 'N/A'],
          ["Uwagi / Comments", workpermit?.comment?.toString() || 'N/A']
        ];

        turbineData.forEach(([label, value]) => {
          const textHeight = measureTextHeight(value, 65);
          const rowHeight = Math.max(12, textHeight + 8);

          doc.rect(10, yPos, 190, rowHeight);
          doc.setFontSize(normalFontSize);
          doc.setFont('Roboto', 'bold');
          doc.text(label, 15, yPos + 6);
          doc.setFont('Roboto', 'normal');
          doc.text(value, 120, yPos + 6, { maxWidth: 75 });
          yPos += rowHeight;
        });

        yPos += 5;
        doc.setFillColor(...styles.grayBg);
        doc.rect(10, yPos, 190, 40, 'F');
        doc.rect(10, yPos, 190, 40);
        doc.setFontSize(normalFontSize);
        doc.setFont('Roboto', 'bold');
        doc.text("Warunki do spelnienia przed rozpoczeciem pracy /", 15, yPos + 8);
        doc.text("Conditions that must be met before the LOTO can take place", 15, yPos + 16);
        yPos += 45;

        return yPos;
      };

      const addThirdPage = () => {
        doc.addPage();
        let yPos = addPageHeader();
        let currentPage = 3;
        const maxYPos = 260;
        const processedSections = new Set();

        const safeText = (text, x, y, options = {}) => {
          const normalizeText = (text) => text?.toString() || '';
          if (!text) return;
          const normalizedText = normalizeText(text);
          if (normalizedText.trim() === '') return;

          if (options.maxWidth) {
            const lines = doc.splitTextToSize(normalizedText, options.maxWidth);
            lines.forEach((line, index) => {
              doc.text(line, x, y + (index * 4));
            });
          } else {
            doc.text(normalizedText, x, y, options);
          }
        };

        const processCellContent = (cell, x, y, width, height) => {
          const lines = doc.splitTextToSize(cell.text?.toString() || '', width - 4);
          const actualTextHeight = lines.length * 4 + 4;
          const finalHeight = Math.max(height, actualTextHeight);

          doc.rect(x, y, width, finalHeight);

          lines.forEach((line, index) => {
            safeText(line, x + 2, y + 4 + (index * 4));
          });

          return finalHeight;
        };

        const addNewPageIfNeeded = () => {
          if (yPos > maxYPos) {
            doc.addPage();
            currentPage++;
            yPos = addPageHeader();

            doc.setFillColor(...styles.blueBg);
            doc.rect(10, yPos, 190, 10, 'F');
            doc.setFontSize(headerFontSize);
            doc.setFont("Roboto", "bold");
            safeText("Zakres wylaczen (kontynuacja)", 15, yPos + 6);
            yPos += 12;

            doc.rect(10, yPos, 20, 20);
            doc.setFontSize(headerFontSize);
            safeText("Kolejnosc /", 12, yPos + 6);
            safeText("Sequence", 12, yPos + 12);

            const headers = addSectionHeaders(yPos);
            yPos = headers.yPos;
          }
        };

        const addSectionHeaders = (startYPos) => {
          let yPos = startYPos;
          const topHeaders = [
            { text: "Punkt izolacji z opisem / Isolation ID and description", width: 75 },
            { text: "Rodzaj i amplituda energi / Type and\nmagnitude of energy", width: 50 },
           
            { text: "Pozycja wylaczenia / Isolation position", width: 45 }
          ];

          let xPos = 30;
          topHeaders.forEach(header => {
            doc.rect(xPos, yPos, header.width, 10);
            safeText(header.text, xPos + 2, yPos + 6);
            xPos += header.width;
          });

          yPos += 10;
          const bottomHeaders = [
            { text: "Metoda weryfikacji odlaczenia energii", width: 50 },
            { text: "Lokalizacja punktow weryfikacji", width: 40 },
            { text: "Metoda rozproszenia energii", width: 45 },
            { text: "Izolacja zakonczona", width: 35 }
          ];

          xPos = 30;
          bottomHeaders.forEach(header => {
            doc.rect(xPos, yPos, header.width, 10);
            safeText(header.text, xPos + 2, yPos + 6);
            xPos += header.width;
          });
          yPos += 10;

          return { yPos };
        };

        doc.setFillColor(...styles.blueBg);
        doc.rect(10, yPos, 190, 10, 'F');
        doc.setFontSize(sectionTitleFontSize);
        doc.setFont("Roboto", "bold");
        safeText("Procedura izolacji / Isolation procedure", 15, yPos + 7);
        yPos += 14;

        doc.setFillColor(...styles.blueBg);
        doc.rect(10, yPos, 190, 10, 'F');
        doc.setFontSize(headerFontSize);
        doc.setFont("Roboto", "bold");
        safeText("Zakres wylaczen", 15, yPos + 6);
        yPos += 12;

        doc.rect(10, yPos, 20, 20);
        doc.setFontSize(headerFontSize);
        safeText("Kolejnosc /", 12, yPos + 6);
        safeText("Sequence", 12, yPos + 12);

        const headers = addSectionHeaders(yPos);
        yPos = headers.yPos;

        if (selectedInstruction && selectedInstruction.data && selectedInstruction.data.sections) {
          selectedInstruction.data.sections.forEach(section => {
            if (!section || !section.steps) return;

            const sectionKey = `${section.mark}-${section.title}`;
            if (!processedSections.has(sectionKey)) {
              processedSections.add(sectionKey);

              addNewPageIfNeeded();

              doc.rect(10, yPos, 190, 10);
              doc.setFontSize(normalFontSize);
              doc.setFont("Roboto", "bold");
              safeText(section.title || 'N/A', 15, yPos + 6);
              yPos += 10;

              if (section.steps && Array.isArray(section.steps)) {
                section.steps.forEach((step, index) => {
                  if (!step) return;

                  const topRowHeights = [
                    { text: step.field1 || '', width: 80 },
                    { text: step.field2 || '-', width: 45 },
                    { text: step.field3 || '-', width: 45 }
                  ].map(cell => {
                    const lines = doc.splitTextToSize(cell.text, cell.width - 4);
                    return lines.length * 4 + 4;
                  });

                  const bottomRowHeights = [
                    { text: step.field4 || "-", width: 55 },
                    { text: step.field5 || "-", width: 45 },
                    { text: step.field6 || "-", width: 45 },
                    { text: "Podpis", width: 25 }
                  ].map(cell => {
                    const lines = doc.splitTextToSize(cell.text, cell.width - 4);
                    return lines.length * 4 + 4;
                  });

                  const maxTopRowHeight = Math.max(...topRowHeights, 12);
                  const maxBottomRowHeight = Math.max(...bottomRowHeights, 12);
                  const totalHeight = maxTopRowHeight + maxBottomRowHeight;

                  if (yPos + totalHeight > maxYPos) {
                    addNewPageIfNeeded();
                  }

                  doc.rect(10, yPos, 20, totalHeight);
                  doc.setFontSize(normalFontSize);
                  doc.setFont("Roboto", "bold");
                  safeText(section.mark + (index + 1), 20, yPos + totalHeight / 2, { align: 'center' });

                  let currentX = 30;
                  [
                    { text: step.field1 || '', width: 80 },
                    { text: step.field2 || '-', width: 45 },
                    { text: step.field3 || '-', width: 45 }
                  ].forEach(cell => {
                    processCellContent(cell, currentX, yPos, cell.width, maxTopRowHeight);
                    currentX += cell.width;
                  });

                  currentX = 30;
                  yPos += maxTopRowHeight;
                  [
                    { text: step.field4 || "-", width: 55 },
                    { text: step.field5 || "-", width: 45 },
                    { text: step.field6 || "-", width: 45 },
                    { text: "Podpis", width: 25 }
                  ].forEach(cell => {
                    processCellContent(cell, currentX, yPos, cell.width, maxBottomRowHeight);
                    currentX += cell.width;
                  });

                  yPos += maxBottomRowHeight + 2;
                });

                doc.rect(10, yPos, 190, 12);

                const summaryCell = {
                  text: `Koniec zakresu: ${section.title}`,
                  width: 170
                };
                const signatureCell = {
                  text: "Podpis",
                  width: 20
                };

                processCellContent(summaryCell, 10, yPos, summaryCell.width, 12);
                processCellContent(signatureCell, 180, yPos, signatureCell.width, 12);

                yPos += 14;
              }
            }
          });
        }

        addNewPageIfNeeded();
        yPos += 10;
        doc.setFillColor(...styles.blueBg);
        doc.rect(10, yPos, 190, 10, 'F');
        doc.setFontSize(sectionTitleFontSize);
        doc.setFont("Roboto", "bold");
        safeText("Procedura ponownego zalaczenia / Restoration Procedure", 15, yPos + 7);
        yPos += 12;

        const restorationColumns = [
          { text: "Kolejnosc /\nSequence", width: 30 },
          { text: "Punkt izolacji wraz z opisem /\nIsolation ID and Description", width: 80 },
          { text: "Pozycja zalaczenia /\nRestore position", width: 40 },
          { text: "Zalaczenie zakonczone /\nRestoration completed", width: 40 }
        ];

        let xPos = 10;
        restorationColumns.forEach(col => {
          doc.rect(xPos, yPos, col.width, 15);
          doc.setFontSize(normalFontSize);
          doc.setFont("Roboto", "bold");
          safeText(col.text, xPos + 2, yPos + 6);
          xPos += col.width;
        });

        yPos += 15;
        xPos = 10;
        restorationColumns.forEach((col, index) => {
          doc.rect(xPos, yPos, col.width, 15);
          if (index === 0) {
            safeText("1", xPos + 5, yPos + 8);
          }
          if (index === 3) {
            doc.setFontSize(8);
            safeText("Person in charge", xPos + 2, yPos + 6);
            safeText("initials and time", xPos + 2, yPos + 11);
          }
          xPos += col.width;
        });

        yPos += 15;
        doc.rect(10, yPos, 190, 10);
        doc.setFontSize(normalFontSize);
        doc.setFont("Roboto", "bold");
        safeText("BRAK KOLEJNYCH PUNKTOW ZALACZENIA", 105, yPos + 6, { align: 'center' });

        return currentPage;
      };

      console.log('Generating pages');
      addFirstPage();
      addSecondPage();
      const pagesCount = addThirdPage();
      setTotalPages(pagesCount);

      console.log('Adding page numbers');
      for (let i = 1; i <= pagesCount; i++) {
        doc.setPage(i);
        doc.setFont('Roboto', 'normal');
        doc.setFontSize(7);
        doc.text(`${i}/${pagesCount}`, 195, 287);
      }

      console.log('Creating PDF blob');
      const blob = doc.output('blob');
      cleanup();
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setPdfBlob(blob);
      if (onPdfGenerated) {
        // Przekaż blob i ID wybranej instrukcji
        onPdfGenerated(blob, selectedInstruction.id);
      }
    } catch (error) {
      console.error('Błąd generowania PDF:', error);
      setError('Nie udało się wygenerować PDF');
      setPdfUrl(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
  if (instructions && instructions.length === 1 && selectedInstructionType === null) {
    const instruction = instructions[0];
    const type = instruction.proper ? 'proper' : 'default';
    setSelectedInstructionType(type);
  }
  // Dla dwóch instrukcji nie ustawiamy domyślnego typu, pozostawiamy pustą wartość
}, [instructions]);

  useEffect(() => {
  if (selectedInstructionType && selectedInstructionType !== '' && instructions) {
    generatePDF();
  }
}, [selectedInstructionType]);

  const handleInstructionTypeChange = (value) => {
    setSelectedInstructionType(value);
    // Reset PDF when changing instruction type
    cleanup();
    setPdfUrl(null);
    setPdfBlob(null);
    if (value && value !== '') {
      generatePDF();
    }
  };

  const handleDownloadPdf = () => {
    if (pdfBlob) {
      const link = document.createElement('a');
      link.href = URL.createObjectURL(pdfBlob);
      link.download = getAttachmentFileName();
      link.click();
    }
  };

  const instructionCount = instructions ? instructions.length : 0;

  // Get selected instruction ID
  const selectedInstruction = instructions?.find(instruction =>
    selectedInstructionType === 'proper' ? instruction.proper : !instruction.proper
  );
  const instructionId = selectedInstruction ? selectedInstruction.id : (instructions?.length > 0 ? instructions[0].id : 'N/A');

  // Function to navigate to edit page
  const getEditLink = () => {
    return `/ecc/turbins?workpermitId=${workpermit?.id}&turbineId=${workpermit?.turbine}&workScopeId=${workpermit?.work_scope}&instructionId=${instructionId}`;
  };

  return (
    <div className="flex-1 overflow-auto p-4 md:p-6">
      <div className="space-y-4">
        {instructionCount === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 bg-white rounded-2xl border border-steel-200 p-8">
            <h2 className="text-xl font-semibold mb-4">No instruction for this case</h2>
            <Link
              to={getEditLink()}
              className="px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
            >
              Write Instruction
            </Link>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-steel-200 overflow-hidden">
            <div className="p-4 border-b border-steel-200 flex items-center justify-between">
              <div className="flex items-center">
                {instructionCount > 1 ? (
                  <div className="relative">
                    <select
                      id="instruction-type"
                      className="w-auto px-3 py-2 pr-8 border border-steel-300 rounded-full appearance-none focus:outline-none focus:ring-2 focus:ring-primary"
                      value={selectedInstructionType || ''}
                      onChange={(e) => handleInstructionTypeChange(e.target.value)}
                    >
                      <option value="" disabled>Choose Instruction Type</option>
                      <option value="default">Default Instruction</option>

                      <option value="proper">Unique Instruction</option>
                    </select>
                    
                  </div>
                ) : (
                  <span className="text-sm font-medium">
                    {instructions[0]?.proper ? 'Unique Instruction' : 'Default Instruction'}
                  </span>
                )}
              </div>
              <Link
                to={getEditLink()}
                className="px-4 py-1.5 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors whitespace-nowrap"
              >
                Edit
              </Link>
            </div>
            
            <div className="p-4">
              {isLoading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
                  <span className="ml-2">Generating PDF...</span>
                </div>
              ) : error ? (
                <div className="flex justify-center items-center h-64 text-red-500">
                  <p>{error}</p>
                </div>
              ) : pdfUrl ? (
                <div className="border border-steel-200 rounded-lg overflow-hidden">
                  <div className="bg-steel-100 p-2 flex items-center justify-between">
                    <div className="flex items-center">
                      <FileText className="h-5 w-5 text-graphite-500 mr-2" />
                      <span className="text-sm text-graphite-700 truncate">
                        {instructionId}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-graphite-700">{currentPage} / {totalPages}</span>
                      <button 
                        className="p-1 text-graphite-700 hover:bg-steel-200 rounded"
                        onClick={() => window.open(pdfUrl, '_blank')}
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                      <button 
                        className="p-1 text-graphite-700 hover:bg-steel-200 rounded"
                        onClick={handleDownloadPdf}
                      >
                        <Download className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="bg-graphite-800 p-4 flex items-center justify-center min-h-[500px]">
                    <iframe
                      ref={pdfViewerRef}
                      src={pdfUrl}
                      title="Work Permit PDF"
                      className="w-full h-[500px] border-0"
                      onLoad={() => setCurrentPage(1)}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex justify-center items-center h-64">
                  {instructionCount === 2 && !selectedInstructionType ? (
                    <p>Select an instruction type to generate the preview</p>
                  ) : (
                    <p>No preview available</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ECCWorkPermitPDFViewer;